import {format} from 'date-fns'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {formatJobTime, parseApiDate} from 'src/jobs/job-schedule-format'

import {formatAssetAssignment} from './format-asset-assignment'
import type {DispatchReportJob} from './schemas'

const SHORT_DATE_FORMAT = 'MM/dd/yy'

export function formatReportDateLabel(reportDate: string) {
    const [yearPart, monthPart, dayPart] = reportDate.split('-')
    const year = Number(yearPart)
    const month = Number(monthPart)
    const day = Number(dayPart)
    return format(new Date(year, month - 1, day), SHORT_DATE_FORMAT)
}

export function formatCreatedAtLabel(createdAt: Date) {
    return formatBusinessTimestamp(createdAt, SHORT_DATE_FORMAT)
}

export function formatScheduleStartLabel(startDate: string, startTime: string | null) {
    const dateLabel = format(parseApiDate(startDate), SHORT_DATE_FORMAT)
    return startTime ? `${dateLabel} ${formatJobTime(startTime)}` : dateLabel
}

export function formatScheduleEndLabel(endDate: string | null) {
    return endDate ? format(parseApiDate(endDate), SHORT_DATE_FORMAT) : ''
}

export function countWorkerAssignments(job: DispatchReportJob) {
    return job.workerAssignments.length
}

export function countEquipmentAssignments(job: DispatchReportJob) {
    return job.assetAssignments.reduce((total, assignment) => total + (assignment.quantity ?? 1), 0)
}

export function formatReportStatusLabel(status: DispatchReportJob['status']) {
    return status === 'active' ? 'Active' : 'Planned'
}

export function formatRequirementsLines(job: DispatchReportJob, quantitySeparator = '×') {
    return [
        ...job.workerRequirements.map(
            (item) => `${item.quantity}${quantitySeparator} ${item.workerTypeName}`,
        ),
        ...job.assetRequirements.map(
            (item) => `${item.quantity}${quantitySeparator} ${item.rentalAssetTypeName}`,
        ),
    ]
}

export function formatAssignmentLines(job: DispatchReportJob, quantitySeparator = '×') {
    return [
        ...job.workerAssignments.map((item) => item.workerName),
        ...job.assetAssignments.map((item) => formatAssetAssignment(item, quantitySeparator)),
    ]
}

export function formatSitesText(job: DispatchReportJob) {
    if (job.locations.length === 0) {
        return ''
    }

    return job.locations
        .map((location) =>
            location.notes ? `${location.name} (${location.notes})` : location.name,
        )
        .join('; ')
}

export function formatJobNotesText(job: DispatchReportJob) {
    return job.jobComments.map((comment) => comment.content).join('; ')
}
